// Copyright ©️ 2026 eVolve MEP, LLC

import type { ReactNode } from 'react';

import { Input, type InputWrapperProps, Toggle, type ToggleProps, SimpleGrid, getSize, Loader } from '@mantine/core';

import { isNil } from 'helpers/isNotNil';
import { TriggerOnVisible } from 'hooks/useOnScreen';

export type ToggleRadioGroupOption<T, K extends string> = {
  id: K;
  value: T;
  label: ReactNode;
  disabled?: boolean;
};

export type ToggleRadioGroupProps<T, K extends string> = Pick<
  InputWrapperProps,
  'label' | 'description' | 'error' | 'required'
> &
  Pick<ToggleProps, 'size' | 'variant' | 'disabled'> & {
    value?: T | null;
    onChange: (value: T | null) => void;
    data: readonly ToggleRadioGroupOption<T, K>[];
    /**
     * The number of columns to display the toggles in.
     * When not provided, the number of options is used.
     */
    cols?: number;
    /** TODO: make this functional */
    loading?: boolean;
    /** If provided, renders a `TriggerOnVisible` component that calls this function when the group becomes visible. */
    onVisible?: () => void;
  };

/**
 * A single-select row of `Toggle`s, for picking one of a few options at a glance.
 */
export const ToggleRadioGroup = <T, K extends string>({
  value,
  onChange,
  data,
  label,
  description,
  error,
  required,
  disabled,
  cols,
  size = 'xl',
  variant,
  loading,
  onVisible,
}: ToggleRadioGroupProps<T, K>) => (
  <Input.Wrapper label={label} description={description} error={error} required={required}>
    <SimpleGrid
      role="radiogroup"
      aria-label={typeof label === 'string' ? label : undefined}
      cols={cols ?? data.length}
      spacing="xs"
      verticalSpacing="xs"
    >
      {data.map((option) => (
        <Toggle
          key={option.id}
          role="radio"
          aria-checked={option.value === value}
          data-active={option.value === value || undefined}
          disabled={disabled || option.disabled}
          active={option.value === value}
          w="100%"
          h="auto"
          mih="var(--toggle-size)"
          size={`calc(${getSize(size, 'toggle-size')} + 1rem)`}
          onActiveChange={() => {
            if (option.value !== value) onChange(option.value);
          }}
          variant={variant}
        >
          {option.label}
        </Toggle>
      ))}
      {onVisible && <TriggerOnVisible onVisible={onVisible} loading={loading} />}
      {isNil(onVisible) && loading && <Loader type="dots" m="xs" />}
    </SimpleGrid>
  </Input.Wrapper>
);
